import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, Subject, map } from 'rxjs';
import { ICity } from '@pulpo/pulpo-models';
import ApiService from './api.service.abstract';
import { APP_CONFIG } from '@pulpo/app-config';
import { CacheBucket, withCache } from '@ngneat/cashew';

@Injectable({ providedIn: 'root' })
export class CityService extends ApiService<ICity> {
  citiesChangedSubject: Subject<ICity[]> = new Subject<ICity[]>();
  cityBucket = new CacheBucket();

  constructor(
    @Inject(APP_CONFIG) environment: any,
    protected http: HttpClient
  ) {
    super(environment, http, 'trip/api/geo/cities');
    if (environment.name === 'localDev') {
      this.url = 'http://localhost:8082';
    }
  }

  public findAllWithCache(): Observable<HttpResponse<ICity[]>> {
    return this.http
      .get<ICity[]>(`${this.url}/${this.endpoint}`, {
        observe: 'response',
        context: withCache({
          bucket: this.cityBucket,
          key: 'cities',
          ttl: 15 * 60 * 1000,
        }),
      })
      .pipe(map((res) => this.mapCities(res)));
  }

  public override find(id: number | string): Observable<HttpResponse<ICity>> {
    return this.http
      .get<ICity>(`${this.url}/${this.endpoint}/${id}`, { observe: 'response' })
      .pipe(map((res) => (res.body ? res.clone({ body: this.withNestedCountry(res.body) }) : res)));
  }

  public findAllByCountry(countryId: number): Observable<HttpResponse<ICity[]>> {
    return this.http
      .get<ICity[]>(`${this.url}/${this.endpoint}/by-country/${countryId}`, { observe: 'response' })
      .pipe(map((res) => this.mapCities(res)));
  }

  public findAllByDestination(destinationId: number): Observable<HttpResponse<ICity[]>> {
    return this.http
      .get<ICity[]>(`${this.url}/${this.endpoint}/by-destination/${destinationId}`, {
        observe: 'response',
      })
      .pipe(map((res) => this.mapCities(res)));
  }

  private mapCities(res: HttpResponse<ICity[]>): HttpResponse<ICity[]> {
    if (!res.body) return res;
    const mapped = res.body.map((c) => this.withNestedCountry(c));
    return res.clone({ body: mapped });
  }

  /** Trip returns flat countryId/countryCode/countryName — backfill nested country for compat. */
  private withNestedCountry(city: ICity): ICity {
    if (city.countryId && !city.country) {
      return {
        ...city,
        country: { id: city.countryId, codeCountry: city.countryCode, name: city.countryName },
      };
    }
    return city;
  }
}
